/**
 * Contrapartes: seguradoras e bancos.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type Address, type ListParams } from './comum';

import { api, requestPage } from '../client';

export type InsurerStatus = 'ativa' | 'em_homologacao' | 'inativa';

export interface Insurer {
  id: string;
  number: number;
  legal_name: string;
  trade_name: string;
  document: string;
  susep_code: string;
  status: InsurerStatus;
  email: string;
  phone: string;
  contact_name: string;
  notes: string;
  product_ids: string[];
  created_at: string;
  updated_at: string;
  // Endereco e atendimento impressos na apolice (P-MIN-2). Opcionais para que a
  // resposta de antes continue valida nos testes.
  address?: Address;
  website?: string;
  /** Central de atendimento. */
  service_phone?: string;
  ombudsman_phone?: string;
  /** Horario da ouvidoria, como impresso. */
  ombudsman_hours?: string;
}

export interface InsurerInput {
  legal_name: string;
  trade_name?: string;
  document?: string;
  susep_code?: string;
  status?: InsurerStatus;
  email?: string;
  phone?: string;
  contact_name?: string;
  notes?: string;
  product_ids?: string[];
  address?: Address;
  website?: string;
  service_phone?: string;
  ombudsman_phone?: string;
  ombudsman_hours?: string;
}

export type BankStatus = 'ativo' | 'em_homologacao' | 'inativo';

export interface Bank {
  id: string;
  number: number;
  name: string;
  code: string;
  document: string;
  status: BankStatus;
  email: string;
  phone: string;
  contact_name: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface BankInput {
  name: string;
  code?: string;
  document?: string;
  status?: BankStatus;
  email?: string;
  phone?: string;
  contact_name?: string;
  notes?: string;
}

export const insurers = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Insurer>(`/api/v1/insurers${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<Insurer>(`/api/v1/insurers/${id}`, signal),
  create: (input: InsurerInput) => api.post<Insurer>('/api/v1/insurers', input),
  update: (id: string, input: InsurerInput) =>
    api.patch<Insurer>(`/api/v1/insurers/${id}`, input),
};

export const banks = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Bank>(`/api/v1/banks${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<Bank>(`/api/v1/banks/${id}`, signal),
  create: (input: BankInput) => api.post<Bank>('/api/v1/banks', input),
  update: (id: string, input: BankInput) =>
    api.patch<Bank>(`/api/v1/banks/${id}`, input),
};

// --- Filiais da seguradora (P-MIN-2) -------------------------------------------

export type InsurerBranchStatus = 'ativa' | 'inativa';

/** Filial da seguradora: o que a apolice imprime em FILIAL. */
export interface InsurerBranch {
  id: string;
  insurer_id: string;
  name: string;
  code: string;
  city: string;
  state: string;
  status: InsurerBranchStatus;
  notes: string;
  created_label: string;
  updated_label: string;
  created_at: string;
  updated_at: string;
}

export interface InsurerBranchInput {
  name?: string;
  code?: string;
  city?: string;
  state?: string;
  status?: InsurerBranchStatus;
  notes?: string;
}

export const insurerBranches = {
  list: (insurerId: string, status?: InsurerBranchStatus, signal?: AbortSignal) =>
    api.get<InsurerBranch[]>(
      `/api/v1/insurers/${insurerId}/branches${status ? `?status=${status}` : ''}`,
      signal,
    ),
  create: (insurerId: string, input: InsurerBranchInput) =>
    api.post<InsurerBranch>(`/api/v1/insurers/${insurerId}/branches`, input),
  update: (insurerId: string, branchId: string, input: InsurerBranchInput) =>
    api.patch<InsurerBranch>(
      `/api/v1/insurers/${insurerId}/branches/${branchId}`,
      input,
    ),
};
